import {
  PackageIcon,
  BanknoteIcon,
  TruckIcon,
  ClockIcon,
  CheckCircle2Icon,
  TrendingUpIcon,
} from "lucide-react";
import { Can } from "@/features/auth/components/Can";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { ACTIONS } from "@/constants/roles";
import { KpiCard } from "@/features/dashboard/components/KpiCard";
import { RevenueTicketChart } from "@/features/dashboard/components/charts/RevenueTicketChart";
import { RentalsByDayChart } from "@/features/dashboard/components/charts/RentalsByDayChart";
import { RentalStatusChart } from "@/features/dashboard/components/charts/RentalStatusChart";
import { CommissionTable } from "@/features/dashboard/components/CommissionTable";
import { OperationalAlerts } from "@/features/dashboard/components/OperationalAlerts";
import { useDashboardData } from "@/features/dashboard/hooks/useDashboardData";

function formatBRL(value: number): string {
  return value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function formatNumber(value: number): string {
  return value.toLocaleString("pt-BR");
}

export function DashboardPage() {
  const data = useDashboardData();
  const { hasPermission } = useAuth();
  const canSeeFinancials = hasPermission(ACTIONS.commissions.read);

  const totalRevenue = data.rentalSummary?.totalRevenue ?? 0;
  const ticketMedio =
    data.totalRentals > 0 ? totalRevenue / data.totalRentals : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-foreground">Dashboard</h1>
        <p className="text-muted-foreground">
          Semana atual — segunda-feira até hoje
        </p>
      </div>

      {/* Alertas operacionais */}
      <OperationalAlerts
        overdueRentals={data.overdueRentals}
        maintenanceCount={data.maintenanceCount}
        loading={data.loading}
      />

      {/* Error */}
      {data.error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {data.error}
        </div>
      )}

      {/* KPI Cards */}
      <div
        className={`grid gap-4 ${canSeeFinancials ? "grid-cols-2 md:grid-cols-3" : "grid-cols-2 md:grid-cols-4"}`}
      >
        <KpiCard
          title="Locações na semana"
          value={formatNumber(data.totalRentals)}
          subtitle="Criadas desde segunda-feira"
          icon={<PackageIcon className="size-4" />}
          loading={data.loading}
        />
        <Can permission={ACTIONS.commissions.read}>
          <KpiCard
            title="Receita"
            value={formatBRL(totalRevenue)}
            subtitle="Total das locações ativas"
            icon={<BanknoteIcon className="size-4" />}
            loading={data.loading}
            accent="success"
          />
        </Can>
        <Can permission={ACTIONS.commissions.read}>
          <KpiCard
            title="Ticket médio"
            value={formatBRL(ticketMedio)}
            subtitle="Receita ÷ locações"
            icon={<TrendingUpIcon className="size-4" />}
            loading={data.loading}
          />
        </Can>
        <KpiCard
          title="Na rua"
          value={formatNumber(data.rentalSummary?.onStreetCount ?? 0)}
          subtitle="Peças fora do estoque"
          icon={<TruckIcon className="size-4" />}
          loading={data.loading}
          accent={
            (data.rentalSummary?.onStreetCount ?? 0) > 0 ? "info" : "default"
          }
        />
        <KpiCard
          title="Incompletos"
          value={formatNumber(data.rentalSummary?.incompleteCount ?? 0)}
          subtitle="Aguardando peças"
          icon={<ClockIcon className="size-4" />}
          loading={data.loading}
          accent={
            (data.rentalSummary?.incompleteCount ?? 0) > 0
              ? "warning"
              : "default"
          }
        />
        <KpiCard
          title="Devolvidos"
          value={formatNumber(data.completedRentals)}
          subtitle="Finalizados na semana"
          icon={<CheckCircle2Icon className="size-4" />}
          loading={data.loading}
          accent={data.completedRentals > 0 ? "success" : "default"}
        />
      </div>

      {/* Revenue + Ticket — admin only */}
      <Can permission={ACTIONS.commissions.read}>
        <RevenueTicketChart data={data.dailyData} loading={data.loading} />
      </Can>

      {/* Rentals by day + Status */}
      <div className="grid gap-4 lg:grid-cols-2">
        <RentalsByDayChart data={data.dailyData} loading={data.loading} />
        <RentalStatusChart
          data={data.currentStatusCounts}
          loading={data.loading}
        />
      </div>

      {/* Commission table — admin only */}
      <Can permission={ACTIONS.commissions.read}>
        <CommissionTable data={data.commissions} loading={data.loading} />
      </Can>
    </div>
  );
}
